import { useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { DetailDrawer } from '@/components/domain/DetailDrawer';
import { Thumbnail } from '@/components/domain/Thumbnail';
import { formatRelative } from '@/lib/time';

type CardCollection = 'submissions' | 'teamStores';

type PipelineCard = {
  id: string;
  collection: CardCollection;
  title: string;
  sport: string;
  status: string;
  at: string | null;
  thumbPath?: string | null;
};

type PipelineColumn = {
  key: string;
  label: string;
  items: PipelineCard[];
};

type Payload = {
  columns: PipelineColumn[];
  total: number;
};

type DetailPayload = Record<string, unknown> & { school_name?: string; schoolName?: string; fundraiserName?: string; storeNumberDisplay?: string };

const STATUS_TONE: Record<string, string> = {
  pending_worker_review: 'bg-bg text-muted',
  awaiting_rep_decision: 'bg-primary/10 text-primary',
  changes_requested:     'bg-warning/10 text-warning',
  final_approved:        'bg-success/10 text-success',
  proof_approved:        'bg-success/10 text-success',
  approved:              'bg-warning/10 text-warning',
  creating_products:     'bg-primary/10 text-primary',
  live:                  'bg-success/10 text-success',
};

export default function Pipeline() {
  const [openCard, setOpenCard] = useState<PipelineCard | null>(null);

  const { data, isLoading } = useSWR<Payload>('/api/pipeline', get);

  const detailUrl = openCard
    ? `/api/${openCard.collection === 'submissions' ? 'submissions' : 'active-stores'}/${openCard.id}`
    : null;
  const { data: detail, isLoading: detailLoading } = useSWR<DetailPayload>(detailUrl, get);

  const thumbnailFields = openCard?.collection === 'submissions'
    ? ['tier1LogoPath', 'tier2LogoPath', 'tier1MockupPath', 'tier2MockupPath']
    : ['logoPath'];

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Pipeline</h1>
      <p className="mb-4 text-[12px] text-muted">
        Fundraiser lifecycle across submissions and team stores · {data?.total ?? 0} in flight
      </p>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-md bg-surface p-3 shadow-card">
              <Skeleton className="mb-3 h-4 w-2/3" />
              <Skeleton className="mb-2 h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {data?.columns.map((col) => (
            <Column key={col.key} col={col} onCardClick={setOpenCard} />
          ))}
        </div>
      )}

      <DetailDrawer
        open={!!openCard}
        onClose={() => setOpenCard(null)}
        title={detail?.schoolName || detail?.school_name || detail?.fundraiserName || openCard?.title || 'Detail'}
        subtitle={detail?.storeNumberDisplay || openCard?.id}
        loading={detailLoading}
        data={detail}
        thumbnailFields={thumbnailFields}
      />
    </div>
  );
}

function Column({ col, onCardClick }: { col: PipelineColumn; onCardClick: (c: PipelineCard) => void }) {
  return (
    <div className="rounded-md bg-surface p-3 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted">{col.label}</h2>
        <span className="rounded-sm bg-bg px-1.5 py-0.5 text-[10px] font-medium text-text">{col.items.length}</span>
      </div>
      {col.items.length === 0 ? (
        <div className="rounded-sm border border-dashed border-border px-2 py-4 text-center text-[11px] text-subtle">Empty</div>
      ) : (
        <div className="space-y-1.5">
          {col.items.map((item) => (
            <button
              key={`${item.collection}:${item.id}`}
              onClick={() => onCardClick(item)}
              className="flex w-full items-center gap-2.5 rounded-[8px] bg-bg/40 p-2 text-left transition-colors hover:bg-bg"
            >
              {item.thumbPath && <Thumbnail path={item.thumbPath} alt="" className="h-9 w-9 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-medium text-text">{item.title}</div>
                <div className="mt-0.5 truncate text-[10.5px] text-muted">
                  {item.sport ? `${item.sport} · ` : ''}{item.at ? formatRelative(item.at) : ''}
                </div>
                <Badge className={`mt-1 inline-flex ${STATUS_TONE[item.status] || 'bg-bg text-muted'}`}>{item.status}</Badge>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
